import {
  IAnyNode,
  IBranchNode,
  ICreatableEntityHandler,
  ICreatableNode,
  IEntityNode,
  IEntitySelectionLabelProps,
  IInlineBranchNode,
  IPresentationNode,
  IRootNode,
  ISearchTagPayload,
  ISearchTagsState,
  ISelectableNode,
  ISelectedEntity,
  ISubsetNode,
  IValidatedNestedField,
  IWithChildren,
} from './types';
import { UnreachableCaseError } from 'ts-essentials';
import {
  BucketKind,
  EventKind,
  FieldType,
  IFieldFieldsFragment,
  ITagBucketDefInput,
  ITagFieldInput,
  ITagLeafField,
  ITagMeasureInput,
  ITagOptionInput,
  IVirtualEventFieldsFragment,
  TagOption,
} from '../../../utils/apollo/graphqlSchema';
import { MaybeNil } from '../../../utils/interfaces';
import { isNil } from 'lodash';
import { assertCondition, assertNotNil } from '../../../utils/types';

/**
 * Returns a unique ID of a given selectable node, considering the type (some IDs/keys may clash across the diffrent
 * kind of entities)
 */
export const getNodeId = (node: ISelectableNode) => {
  if ('id' in node) {
    return `${node.__typename}-${node.id}`;
  } else if ('key' in node) {
    return `${node.__typename}-${node.key}`;
  } else if (node.__typename === 'TagBranch') {
    return `${node.__typename}-${node.name}`;
  } else if (node.__typename === 'TagLeafCustomBucketDef') {
    return `${node.__typename}-${node.kind}`;
  } else if (node.__typename === '__CreatableNode') {
    return node.__typename;
  } else {
    throw new UnreachableCaseError(node);
  }
};

/**
 * Produce React-compatible key of the given node to use it in lists or to control mount/unmount behaviour
 */
export const getSelectableNodeKey = (node: ISelectableNode) =>
  `key-${getNodeId(node)}`;

/**
 * Same as [getSelectableNodeKey] but also supports presentation elements. They don't have unique identifier,
 * that's why we need an index
 */
export const getAnyNodeKey = (node: IAnyNode, index: number) => {
  if (isPresentationNode(node)) {
    return `key-separator-${index}`;
  } else {
    return getSelectableNodeKey(node);
  }
};

/**
 * Compare if 2 nodes are equal
 */
export const areNodesEqual = (a: IAnyNode, b: IAnyNode) => {
  if (isSelectableNode(a) && isSelectableNode(b)) {
    return getNodeId(a) === getNodeId(b);
  } else {
    return false;
  }
};

/**
 * Helper to create ITagOptionInput because some tags have additional params
 */
export const createTagOptionInput = (
  tag: TagOption,
  optionParams?: {
    field?: ITagFieldInput;
    measure?: ITagMeasureInput;
    bucketDef?: ITagBucketDefInput;
  },
): ITagOptionInput => ({
  tag,
  field: optionParams?.field ?? null,
  measure: optionParams?.measure ?? null,
  bucketDef: optionParams?.bucketDef ?? null,
});

/**
 * Create an unique ID for each SelectedEntity considering they could be different kinds
 * @param entity
 */
export const getSelectedEntityUniqId = (entity: ISelectedEntity) => {
  let uniqId: string;
  if (entity.__typename === 'BasicEventValue') {
    uniqId = entity.value.key;
  } else if ('key' in entity) {
    uniqId = entity.key;
  } else if ('id' in entity) {
    uniqId = entity.id;
  } else if (entity.__typename === 'AnyEvent') {
    uniqId = 'any-event';
  } else if (entity.__typename === '__CustomBucketDef') {
    uniqId = entity.kind;
  } else {
    console.error(entity);
    throw new UnreachableCaseError(entity);
  }
  return `${entity.__typename}-${uniqId}`;
};

/**
 * Helper to add a SelectedEntity's to a list but if already exists, it removes it
 *
 * @param existingSelection
 * @param selectedEntity
 */
export const toggleEntityInSelection = <T extends ISelectedEntity>(
  existingSelection: T[],
  selectedEntity: T,
): T[] => {
  const result = [...existingSelection];
  const id = getSelectedEntityUniqId(selectedEntity);
  const currentIndex = result.findIndex(
    (existing) => getSelectedEntityUniqId(existing) === id,
  );
  if (currentIndex === -1) {
    result.push(selectedEntity);
  } else {
    result.splice(currentIndex, 1);
  }
  return result;
};

/**
 * Helper to remove an entity from a list of entities
 *
 * @param existingSelection
 * @param selectedEntity
 */
export const removeEntityFromSelection = <T extends ISelectedEntity>(
  existingSelection: T[],
  selectedEntity: T,
): T[] => {
  const key = getSelectedEntityUniqId(selectedEntity);
  return existingSelection.filter(
    (existing) => getSelectedEntityUniqId(existing) !== key,
  );
};

/**
 * Helper to extract type-safely additional props required to render label in
 * the entity selection widget text box. Different entities have different properties:
 * - Named entities may supply expand handlers
 * - Basic entities my render in different color
 */
export const getLabelPropsForType = <
  UNION_PROPS extends IEntitySelectionLabelProps,
  TYPE extends IEntitySelectionLabelProps['type'],
  NARROWED_PROPS extends Extract<UNION_PROPS, { type: TYPE }>,
>(
  labelProps: MaybeNil<UNION_PROPS[]>,
  type: TYPE,
): NARROWED_PROPS | undefined =>
  labelProps?.find((e): e is NARROWED_PROPS => e.type === type);

/**
 * Helper to curry an expand handler with specific SelectedEntity
 */
export const createOptionalHandler = <D, R>(
  labelProps: { onExpand: MaybeNil<(data: D) => R> } | undefined,
  data: D,
) => {
  const onExpand = labelProps?.onExpand;
  if (isNil(onExpand)) {
    return undefined;
  } else {
    return () => onExpand(data);
  }
};

export const asSingleSelection = <T extends ISelectedEntity>(
  entity: MaybeNil<T>,
): T[] => {
  if (isNil(entity)) {
    return [];
  } else {
    return [entity];
  }
};

/**
 * Narrowing helpers
 */
export const presentationTypes: Record<IPresentationNode['__typename'], true> =
  {
    TagLeafSeparator: true,
  };

export const isPresentationNode = (node: IAnyNode): node is IPresentationNode =>
  node.__typename in presentationTypes;

const entityTypes: Record<IEntityNode['__typename'], true> = {
  TagLeafAnyEvent: true,
  TagLeafAnyTableEvent: true,
  TagLeafBasicEvent: true,
  TagLeafCompoundCohort: true,
  TagLeafCompoundMeasure: true,
  TagLeafCustomBucketDef: true,
  TagLeafDateBucketDef: true,
  TagLeafEnumBucketDef: true,
  TagLeafField: true,
  TagLeafFilter: true,
  TagLeafRangeBucketDef: true,
  TagLeafSimpleCohort: true,
  TagLeafSimpleMeasure: true,
  TagLeafVirtualEvent: true,
};

export const isEntityNode = (node: IAnyNode): node is IEntityNode =>
  node.__typename in entityTypes;

const branchTypes: Record<IBranchNode['__typename'], true> = {
  TagBranch: true,
};

export const isBranchNode = (node: IAnyNode): node is IBranchNode =>
  node.__typename in branchTypes;

const creatableTypes: Record<ICreatableNode['__typename'], true> = {
  __CreatableNode: true,
};

export const isCreatableNode = (node: IAnyNode): node is ICreatableNode =>
  node.__typename in creatableTypes;

const inlineBranchTypes: Record<IInlineBranchNode['__typename'], true> = {
  TagInlineBranch: true,
  TagFieldInlineBranch: true,
};

export const isInlineBranchNode = (node: IAnyNode): node is IInlineBranchNode =>
  node.__typename in inlineBranchTypes;

export const isSelectableNode = (node: IAnyNode): node is ISelectableNode =>
  isEntityNode(node) ||
  isCreatableNode(node) ||
  isBranchNode(node) ||
  isInlineBranchNode(node);

/**
 * Compares SelectedEntity and TagNode that represents such entity. Returns true if TagNode in the tree
 * represents that SelectedEntity
 */
export const isNodeEqualToEntity = (
  node: IAnyNode,
  entity: ISelectedEntity,
): boolean => {
  // Events
  if (
    node.__typename === 'TagLeafAnyEvent' &&
    entity.__typename === 'AnyEvent'
  ) {
    return true;
  } else if (
    node.__typename === 'TagLeafAnyTableEvent' &&
    entity.__typename === 'AnyTableEvent'
  ) {
    return node.key === entity.key;
  } else if (
    node.__typename === 'TagLeafBasicEvent' &&
    entity.__typename === 'BasicEventValue'
  ) {
    return entity.value.key === node.key;
  } else if (
    node.__typename === 'TagLeafVirtualEvent' &&
    entity.__typename === 'VirtualEvent'
  ) {
    return node.id === entity.id;
  }
  // Field
  else if (
    node.__typename === 'TagLeafField' &&
    entity.__typename === 'Field'
  ) {
    return node.key === entity.key;
  } else if (
    (node.__typename === 'TagInlineBranch' ||
      node.__typename === 'TagFieldInlineBranch') &&
    entity.__typename === 'Field'
  ) {
    return node.key === entity.nestedFieldMetadata?.fieldKey;
  }
  // Buckets
  else if (
    node.__typename === 'TagLeafCustomBucketDef' &&
    entity.__typename === '__CustomBucketDef'
  ) {
    return node.kind === entity.kind;
  } else if (
    (node.__typename === 'TagLeafDateBucketDef' ||
      node.__typename === 'TagLeafEnumBucketDef' ||
      node.__typename === 'TagLeafRangeBucketDef') &&
    entity.__typename === 'NamedBucketDef'
  ) {
    return node.id === entity.id;
  }
  // Measures
  else if (
    node.__typename === 'TagLeafSimpleMeasure' &&
    entity.__typename === 'SimpleMeasure'
  ) {
    return node.id === entity.id;
  } else if (
    node.__typename === 'TagLeafCompoundMeasure' &&
    entity.__typename === 'CompoundMeasure'
  ) {
    return node.id === entity.id;
  }
  // Filters
  else if (
    node.__typename === 'TagLeafFilter' &&
    entity.__typename === 'NamedFilter'
  ) {
    return node.id === entity.id;
  }
  // Cohort
  else if (
    node.__typename === 'TagLeafCompoundCohort' &&
    entity.__typename === 'CompoundCohort'
  ) {
    return node.id === entity.id;
  } else if (
    node.__typename === 'TagLeafSimpleCohort' &&
    (entity.__typename === 'ExpressionCohort' ||
      entity.__typename === 'FunnelCohort' ||
      entity.__typename === 'PerformedCohort' ||
      entity.__typename === 'RetentionCohort' ||
      entity.__typename === 'MeasureCohort')
  ) {
    return node.id === entity.id;
  } else {
    return false;
  }
};

export const isNotBlank = (text: MaybeNil<string>): text is string =>
  !isNil(text) && text !== '';

export const createBasicEventHandler: ICreatableEntityHandler = (text) => {
  if (text !== '') {
    return {
      __typename: 'BasicEventValue',
      value: {
        __typename: 'BasicFieldValue',
        name: text,
        key: text,
        description: null,
        isDisabled: false,
      },
      categories: [],
      kind: EventKind.Basic,
      isManaged: false,
      isFavorite: false,
      tableNames: [],
    };
  } else {
    return null;
  }
};

export const isNoDetailsNode = (
  node: IAnyNode,
): node is ISubsetNode<'TagLeafAnyEvent' | '__CreatableNode'> => {
  return (
    node.__typename === 'TagLeafAnyEvent' ||
    node.__typename === '__CreatableNode'
  );
};

export const isStructType = (type: FieldType) => type === FieldType.Struct;

export const canSelectNestedField = (
  selectedEntities: ISelectedEntity[],
  disabledEntities: MaybeNil<ISelectedEntity[]>,
  nestedField: IFieldFieldsFragment,
) => {
  // is it already selected before opening the widget?
  const isEntitySelected = selectedEntities.some(
    (entity) => entity.__typename === 'Field' && entity.key === nestedField.key,
  );

  return (
    (isEntitySelected ||
      // it was a new selection, disallow selection if among disabled entities
      !disabledEntities?.some(
        (entity) =>
          entity.__typename === 'Field' && entity.key === nestedField.key,
      )) ??
    false
  );
};

const nestedFieldKeyPattern = RegExp(
  '^(?<fieldKey>[^:]+):(?<key>[^:]+)::(?<type>[^:]+)$',
);

const toNestedFieldKey = ({ fieldKey, key, type }: IValidatedNestedField) => {
  assertCondition(
    !fieldKey.match(nestedFieldKeyPattern),
    'Field key "' + fieldKey + '" looks like a nested field already',
  );
  const nestedFieldKey = `${fieldKey}:${key}::${type}`;
  assertCondition(
    nestedFieldKey.match(nestedFieldKeyPattern),
    `Produced key ${nestedFieldKey} does not match the nested key pattern`,
  );
  return nestedFieldKey;
};

export const toNestedFieldNode = (
  validatedNestedField: IValidatedNestedField,
): ITagLeafField => {
  const nestedFieldKey = toNestedFieldKey(validatedNestedField);
  return {
    __typename: 'TagLeafField',
    key: nestedFieldKey,
    name: nestedFieldKey,
    isFavorite: false,
    isGeo: false,
  };
};

export const bucketKindLabel: Record<BucketKind, string> = {
  [BucketKind.Enum]: 'Value Binning',
  [BucketKind.Range]: 'Range Binning',
  [BucketKind.Date]: 'Date Binning',
};

/**
 * Converts GraphQL `searchTags` response to a more convenient data structure. Converts `mapping` from the response into
 * referential collection of `children` of Branch Nodes
 */
export const createSearchTagsState = (
  payload: ISearchTagPayload,
): ISearchTagsState => {
  /**
   * Private helpers to aid the state creation.
   * Maintain map of index to BranchNode in order retain the `children` attribute we introduce to the TagBranch
   */
  const branchNodesByIndex = new Map<number, IBranchNode | IInlineBranchNode>();
  const mapNodeByIndex = (index: number): IAnyNode => {
    const node = payload.nodes[index];
    assertNotNil(node);
    // root nodes should be handled separately
    assertCondition(node.__typename !== 'TagRoot');
    if (
      node.__typename === 'TagBranch' ||
      node.__typename === 'TagInlineBranch' ||
      node.__typename === 'TagFieldInlineBranch'
    ) {
      let branchNode = branchNodesByIndex.get(index);
      if (isNil(branchNode)) {
        branchNode = {
          ...node,
          children: [],
        };
        branchNodesByIndex.set(index, branchNode);
      }
      return branchNode;
    } else {
      return node;
    }
  };

  const root: IRootNode = { children: [] };
  for (const { childrenIndex, parentIndex } of payload.mapping) {
    const parent = payload.nodes[parentIndex];
    let parentNode: IWithChildren;
    if (parent.__typename === 'TagRoot') {
      parentNode = root;
    } else {
      const node = mapNodeByIndex(parentIndex);
      assertCondition(isBranchNode(node) || isInlineBranchNode(node));
      parentNode = node;
    }
    parentNode.children = childrenIndex.map((childIndex) =>
      mapNodeByIndex(childIndex),
    );
  }

  let defaultNode: IEntityNode | null = null;
  if (!isNil(payload.defaultNodeIndex)) {
    const defaultAnyNode = mapNodeByIndex(payload.defaultNodeIndex);
    if (isEntityNode(defaultAnyNode)) {
      defaultNode = defaultAnyNode; // now the node is entity
    }
  }

  return {
    root,
    defaultNode,
  };
};

export const extractTableNamesVirtualEvent = (
  entity: IVirtualEventFieldsFragment,
): string[] => {
  const tableNames = entity.eventsConditions.flatMap((el) =>
    el.events.flatMap((event) => {
      if (event.__typename === 'BasicEventValue') {
        return event.tableNames;
      } else if (event.__typename === 'VirtualEvent') {
        throw new Error(
          'Nested VirtualEvent inside VirtualEvent is not supported.',
        );
      } else if (
        event.__typename === 'AnyEvent' ||
        event.__typename === 'AnyTableEvent'
      ) {
        return [];
      } else {
        throw new UnreachableCaseError(event);
      }
    }),
  );

  return Array.from(new Set(tableNames)).sort();
};
